import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { Nav } from "reactstrap";
import { useCompanyCore } from "../../../cores/company";
import { useGlobalCore } from "../../../cores/globals";
import { useMeCore } from "../../../cores/me";
import { useRouterCore } from "../../../cores/router";
import { PlanBadge } from "../companies/PlanBadge";
export type CompanySelectorLayout = "default" | "mobile";

type Props = {
  /** En móvil: ancho completo y sin badge para no comprimir el select */
  layout?: CompanySelectorLayout;
};

export const CompanySelector = ({ layout = "default" }: Props) => {
  const { reset, isDarkMode } = useGlobalCore();
  const { getCompanies, companies: adminCompanies } = useCompanyCore();
  const {
    company,
    companies,
    selectCompany,
    companyPlan,
    isAdmin,
  } = useMeCore();
  const { goToSocial } = useRouterCore();
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );

  useEffect(() => {
    getCompanies({});
  }, [getCompanies]);

  const allCompanies = useMemo(
    () => (isAdmin ? [...adminCompanies] : [...companies]),
    [isAdmin, adminCompanies, companies]
  );

  const onChangeCompany = useCallback(
    (option) => {
      reset();
      const payload = allCompanies.find(
        ({ company: { id } }) => +id === +option.value
      );
      payload && selectCompany(payload.company.id);
      goToSocial();
    },
    [allCompanies, reset, selectCompany, goToSocial]
  );

  const selectedOption = useMemo(
    () => options.find(({ value }) => value === company?.id),
    [company, options]
  );

  const selectStyles = useMemo(
    () => ({
      control: (styles: any) => ({
        ...styles,
        backgroundColor: "transparent",
        border: "none",
        width: "100%",
        minWidth: layout === "mobile" ? 200 : 180,
        minHeight: 44,
        color: isDarkMode ? "#E6EDF3" : styles.color,
        boxShadow: "none",
      }),
      input: (styles: any) => ({
        ...styles,
        color: isDarkMode ? "#E6EDF3" : styles.color,
      }),
      menu: (styles: any) => ({
        ...(isDarkMode ? { ...styles, backgroundColor: "#161B22" } : styles),
        zIndex: 10050,
        /* Con menuPortal + control estrecho, el menú heredaba pocos px de ancho */
        ...(layout === "mobile"
          ? { minWidth: "min(100vw - 32px, 360px)" }
          : {}),
      }),
      menuPortal: (styles: any) => ({
        ...styles,
        zIndex: 10050,
      }),
      valueContainer: (styles: any) => ({
        ...styles,
        flexWrap: "nowrap",
        overflow: "hidden",
      }),
      option: (styles: any, state: any) =>
        isDarkMode
          ? {
              ...styles,
              backgroundColor: state.isFocused ? "#0B0E14" : "#161B22",
              color: "#E6EDF3",
              whiteSpace: "nowrap",
            }
          : { ...styles, whiteSpace: "nowrap" },
      placeholder: (styles: any) => ({
        ...styles,
        color: isDarkMode ? "#8B949E" : styles.color,
      }),
      singleValue: (styles: any) => ({
        ...styles,
        color: isDarkMode ? "#E6EDF3" : styles.color,
        ...(layout === "mobile"
          ? {
              maxWidth: "calc(100% - 28px)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }
          : {}),
      }),
    }),
    [isDarkMode, layout]
  );

  useEffect(() => {
    setOptions(
      allCompanies.map(({ company: { id, name } }) => ({
        value: id,
        label: name,
      }))
    );
  }, [allCompanies, company, isAdmin]);

  return (
    company && (
      <Nav
        navbar
        className={layout === "mobile" ? "w-100 align-items-stretch" : undefined}
      >
        <div
          className={
            layout === "mobile"
              ? "px-2 py-1 d-flex align-items-center company-selector company-selector--mobile w-100"
              : "px-3 py-1 d-flex align-items-center company-selector"
          }
        >
          <Select
            type="select"
            value={selectedOption}
            onChange={onChangeCompany}
            classNamePrefix="company-selector"
            className={layout === "mobile" ? "w-100" : undefined}
            isClearable={false}
            options={options}
            isSearchable
            blurInputOnSelect
            menuPosition="fixed"
            menuPortalTarget={
              typeof document !== "undefined" ? document.body : null
            }
            styles={selectStyles}
          />
          {companyPlan && layout !== "mobile" && (
            <PlanBadge plan={companyPlan} className="ml-2" />
          )}
        </div>
      </Nav>
    )
  );
};
